/**
 * Create / edit dialog for a MANUAL promotion -- phase 6b, Task 2.
 *
 * Mirrors `promotionFields` (types.ts) one input per field. `active` is NOT a
 * field: activation goes through the list's Activate/Deactivate actions
 * (POST /activate|/deactivate), never the PUT body. Nullable inputs left
 * blank submit JSON `null` -- never `""` or `0` (ADMIN_API.md, ruling A-4).
 *
 * Stacking (§8, ruling S-1) is a three-way radio -- don't stack / stack with
 * anything / stack only with... -- where "only" opens a multi-select of the
 * other stackable, active promotions. "only" with no partner disables Save
 * (the API would answer 400 for an empty list).
 *
 * Errors: a 422 lands under the matching input via FIELD_PATHS (path ->
 * input id); entries whose path no input owns (or the whole-body path '')
 * show in the dialog's top Alert. Anything else (409, 400, network) is a
 * snackbar via react-admin's `useNotify`.
 *
 * Money never gets arithmetic here: `toMoneyString` normalises text to the
 * API's 2dp string and THROWS on bad input, which becomes a field error.
 *
 * Task 3 (detail screen) imports this dialog with the same props.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNotify } from 'react-admin';
import { discountsApi, qk } from '../api';
import { toApiFailure } from '../errors';
import { toMoneyString } from '../money';
import type { DiscountType, PromotionDetail, PromotionFields, StackingPartner } from '../types';
import { fromDetail, toStackingInput, unreciprocated, type StackingMode } from './stacking';

export interface PromotionDialogProps {
  open: boolean;
  onClose: () => void;
  /** Present = edit (PUT /promotions/:id); absent = create (POST /promotions, kind MANUAL). */
  initial?: PromotionDetail;
  onSaved?: (p: PromotionDetail) => void;
}

/**
 * 422 `path` -> the input that owns it. Every key is a `promotionFields`
 * field name; the value is the DOM id of its input (helper text renders
 * under that input). `stacking` and `stacking.promotionIds` both land on the
 * partner picker. Paths not listed here (and '') go to the top Alert.
 */
export const FIELD_PATHS: Record<string, string> = {
  name: 'promo-name',
  banner: 'promo-banner',
  discountType: 'promo-discountType',
  discountValue: 'promo-discountValue',
  maxDiscountAmount: 'promo-maxDiscountAmount',
  minOrderAmount: 'promo-minOrderAmount',
  freeItemProductId: 'promo-freeItemProductId',
  freeItemQty: 'promo-freeItemQty',
  startsAt: 'promo-startsAt',
  endsAt: 'promo-endsAt',
  totalRedemptionLimit: 'promo-totalRedemptionLimit',
  perCustomerLimit: 'promo-perCustomerLimit',
  perCustomerDailyLimit: 'promo-perCustomerDailyLimit',
  firstOrderOnly: 'promo-firstOrderOnly',
  returningCustomerOnly: 'promo-returningCustomerOnly',
  priority: 'promo-priority',
  eligibleProductIds: 'promo-eligibleProductIds',
  eligibleCategoryIds: 'promo-eligibleCategoryIds',
  stacking: 'promo-stacking',
  'stacking.promotionIds': 'promo-stacking',
};

const DISCOUNT_TYPES: Array<{ value: DiscountType; label: string }> = [
  { value: 'AMOUNT', label: 'Rand amount off' },
  { value: 'PERCENT', label: 'Percentage off' },
  { value: 'FREE_DELIVERY', label: 'Free delivery' },
  { value: 'FREE_ITEM', label: 'Free item' },
];

/** Everything the inputs hold, as strings (the API body is derived on submit). */
type FormState = {
  name: string;
  banner: string;
  discountType: DiscountType;
  discountValue: string;
  maxDiscountAmount: string;
  minOrderAmount: string;
  freeItemProductId: string;
  freeItemQty: string;
  startsAt: string; // datetime-local
  endsAt: string; // datetime-local
  totalRedemptionLimit: string;
  perCustomerLimit: string;
  perCustomerDailyLimit: string;
  firstOrderOnly: boolean;
  returningCustomerOnly: boolean;
  priority: string;
  eligibleProductIds: string; // one uuid per line / comma
  eligibleCategoryIds: string;
  stackingMode: StackingMode;
  stackingIds: string[];
};

const EMPTY: FormState = {
  name: '',
  banner: '',
  discountType: 'AMOUNT',
  discountValue: '',
  maxDiscountAmount: '',
  minOrderAmount: '',
  freeItemProductId: '',
  freeItemQty: '',
  startsAt: '',
  endsAt: '',
  totalRedemptionLimit: '',
  perCustomerLimit: '',
  perCustomerDailyLimit: '',
  firstOrderOnly: false,
  returningCustomerOnly: false,
  priority: '0',
  eligibleProductIds: '',
  eligibleCategoryIds: '',
  stackingMode: 'none',
  stackingIds: [],
};

const pad = (n: number) => String(n).padStart(2, '0');

/** ISO -> the browser-local `YYYY-MM-DDTHH:mm` a datetime-local input wants. */
export const isoToLocalInput = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** datetime-local -> ISO; blank -> null. */
export const localInputToIso = (local: string): string | null => {
  if (!local.trim()) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const fromInitial = (p: PromotionDetail): FormState => {
  const s = fromDetail(p);
  return {
    name: p.name,
    banner: p.banner ?? '',
    discountType: p.discountType,
    discountValue: p.discountValue ?? '',
    maxDiscountAmount: p.maxDiscountAmount ?? '',
    minOrderAmount: p.minOrderAmount ?? '',
    freeItemProductId: p.freeItemProductId ?? '',
    freeItemQty: p.freeItemQty == null ? '' : String(p.freeItemQty),
    startsAt: isoToLocalInput(p.startsAt),
    endsAt: isoToLocalInput(p.endsAt),
    totalRedemptionLimit: p.totalRedemptionLimit == null ? '' : String(p.totalRedemptionLimit),
    perCustomerLimit: p.perCustomerLimit == null ? '' : String(p.perCustomerLimit),
    perCustomerDailyLimit: p.perCustomerDailyLimit == null ? '' : String(p.perCustomerDailyLimit),
    firstOrderOnly: p.firstOrderOnly,
    returningCustomerOnly: p.returningCustomerOnly,
    priority: String(p.priority),
    eligibleProductIds: (p.eligibleProductIds ?? []).join('\n'),
    eligibleCategoryIds: (p.eligibleCategoryIds ?? []).join('\n'),
    stackingMode: s.mode,
    stackingIds: s.ids,
  };
};

/**
 * Blank -> null; otherwise an integer >= `min`, or a thrown error (surfaced as
 * a field error). The limits and freeItemQty are `int >= 1 | null`: a typed
 * `0` is refused HERE, because the API's nullable coercion would silently turn
 * it into null = unlimited (ADMIN_API.md) -- the opposite of what "0" says.
 */
const intOrNull = (raw: string, label: string, min: number): number | null => {
  const s = raw.trim();
  if (s === '') return null;
  if (!/^\d+$/.test(s)) throw new Error(`${label} must be a whole number`);
  const n = Number(s);
  if (n < min) throw new Error(`${label} must be at least ${min}`);
  return n;
};

/** Comma / whitespace separated ids -> array; nothing -> null. */
const idListOrNull = (raw: string): string[] | null => {
  const ids = raw
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return ids.length ? ids : null;
};

type BuildResult = { body: PromotionFields } | { errors: Record<string, string> };

/** FormState -> `promotionFields` body. Collects local parse errors per path instead of throwing. */
export const buildBody = (f: FormState): BuildResult => {
  const errors: Record<string, string> = {};
  const money = (path: keyof FormState & string, raw: string) => {
    try {
      return toMoneyString(raw);
    } catch (e) {
      errors[path] = (e as Error).message;
      return null;
    }
  };
  const int = (path: keyof FormState & string, raw: string, label: string, min = 1) => {
    try {
      return intOrNull(raw, label, min);
    } catch (e) {
      errors[path] = (e as Error).message;
      return null;
    }
  };

  const isValue = f.discountType === 'AMOUNT' || f.discountType === 'PERCENT';
  const isFreeItem = f.discountType === 'FREE_ITEM';

  const body: PromotionFields = {
    name: f.name.trim(),
    banner: f.banner.trim() === '' ? null : f.banner.trim(),
    discountType: f.discountType,
    discountValue: isValue ? money('discountValue', f.discountValue) : null,
    maxDiscountAmount: money('maxDiscountAmount', f.maxDiscountAmount),
    minOrderAmount: money('minOrderAmount', f.minOrderAmount),
    freeItemProductId: isFreeItem && f.freeItemProductId.trim() !== '' ? f.freeItemProductId.trim() : null,
    freeItemQty: isFreeItem ? int('freeItemQty', f.freeItemQty, 'Quantity') : null,
    startsAt: localInputToIso(f.startsAt),
    endsAt: localInputToIso(f.endsAt),
    totalRedemptionLimit: int('totalRedemptionLimit', f.totalRedemptionLimit, 'Total limit'),
    perCustomerLimit: int('perCustomerLimit', f.perCustomerLimit, 'Per-customer limit'),
    perCustomerDailyLimit: int('perCustomerDailyLimit', f.perCustomerDailyLimit, 'Per-customer daily limit'),
    firstOrderOnly: f.firstOrderOnly,
    returningCustomerOnly: f.returningCustomerOnly,
    priority: int('priority', f.priority, 'Priority', 0) ?? 0,
    eligibleProductIds: idListOrNull(f.eligibleProductIds),
    eligibleCategoryIds: idListOrNull(f.eligibleCategoryIds),
    stacking: toStackingInput(f.stackingMode, f.stackingIds),
  };

  return Object.keys(errors).length ? { errors } : { body };
};

export const PromotionDialog: React.FC<PromotionDialogProps> = ({ open, onClose, initial, onSaved }) => {
  const notify = useNotify();
  const qc = useQueryClient();
  const isEdit = Boolean(initial);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  // Reset ONLY when the dialog opens (or is re-pointed at a different promotion
  // while open). Task 3 passes `initial={detailQuery.data}`; a react-query
  // refetch hands us a NEW object with the same id, and that must not wipe
  // in-progress edits -- so `initial` is snapshotted at open, not tracked.
  useEffect(() => {
    if (!open) return;
    setForm(initial ? fromInitial(initial) : EMPTY);
    setFieldErrors({});
    setFormError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial?.id]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (fieldErrors[key]) setFieldErrors((prev) => ({ ...prev, [key]: '' }));
    // stackingMode / stackingIds don't own a fieldErrors key of their own -- the API's 422 paths
    // are `stacking` and `stacking.promotionIds` (FIELD_PATHS), so clear those here instead.
    if (key === 'stackingMode' || key === 'stackingIds') {
      if (fieldErrors.stacking || fieldErrors['stacking.promotionIds']) {
        setFieldErrors((prev) => ({ ...prev, stacking: '', 'stacking.promotionIds': '' }));
      }
    }
  };

  // Partner picker: other stackable, active promotions (S-1 -- own side only).
  const partnersQuery = useQuery({
    queryKey: qk.promotions({ active: true, limit: 100 }),
    queryFn: () => discountsApi.listPromotions({ active: true, limit: 100 }),
    enabled: open,
  });
  const partnerOptions = useMemo<StackingPartner[]>(() => {
    const fromList = (partnersQuery.data?.data ?? [])
      .filter((p) => p.stackable && p.id !== initial?.id)
      .map((p) => ({ id: p.id, name: p.name }));
    // Keep an already-selected partner selectable even if it dropped out of the active list.
    const known = new Set(fromList.map((p) => p.id));
    const extra = (initial?.stacksWith ?? []).filter((p) => !known.has(p.id));
    return [...fromList, ...extra];
  }, [partnersQuery.data, initial]);
  const selectedPartners = useMemo(
    () => form.stackingIds.map((id) => partnerOptions.find((p) => p.id === id) ?? { id, name: id }),
    [form.stackingIds, partnerOptions],
  );
  const notReciprocated = initial ? unreciprocated(initial) : [];

  const mutation = useMutation({
    mutationFn: (body: PromotionFields) =>
      initial ? discountsApi.updatePromotion(initial.id, body) : discountsApi.createPromotion({ kind: 'MANUAL', ...body }),
    onSuccess: async (saved) => {
      // Whole ['discounts'] prefix: every list, the detail (Task 3), vouchers and the stats bar.
      await qc.invalidateQueries({ queryKey: qk.all });
      notify(isEdit ? 'Promotion updated' : 'Promotion created', { type: 'success' });
      onSaved?.(saved);
      onClose();
    },
    onError: (err) => {
      const f = toApiFailure(err);
      if (f.status === 422) {
        const owned: Record<string, string> = {};
        const orphaned: string[] = [];
        Object.keys(f.fieldErrors).forEach((path) => {
          if (FIELD_PATHS[path]) owned[path] = f.fieldErrors[path];
          else orphaned.push(`${path}: ${f.fieldErrors[path]}`);
        });
        setFieldErrors(owned);
        setFormError(orphaned.length || !Object.keys(owned).length ? orphaned.join(' · ') || f.message : null);
        return;
      }
      notify(f.message, { type: 'error' });
    },
  });

  const handleSubmit = () => {
    const built = buildBody(form);
    if ('errors' in built) {
      setFieldErrors(built.errors);
      return;
    }
    setFormError(null);
    mutation.mutate(built.body);
  };

  const onlyWithNoPartners = form.stackingMode === 'only' && form.stackingIds.length === 0;
  const canSave = !mutation.isPending && form.name.trim().length >= 2 && !onlyWithNoPartners;
  const err = (path: string) => fieldErrors[path] || undefined;
  const isValueType = form.discountType === 'AMOUNT' || form.discountType === 'PERCENT';

  return (
    <Dialog open={open} onClose={mutation.isPending ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? `Edit ${initial?.name ?? 'promotion'}` : 'New promotion'}</DialogTitle>
      <DialogContent dividers>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError(null)}>
            {formError}
          </Alert>
        )}
        {notReciprocated.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Not yet reciprocated (the partner does not permit this promotion back): {notReciprocated.join(', ')}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              id={FIELD_PATHS.name}
              label="Name"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              required
              fullWidth
              slotProps={{ htmlInput: { maxLength: 120 } }}
              error={Boolean(err('name'))}
              helperText={err('name') ?? '2–120 characters'}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              id={FIELD_PATHS.banner}
              label="Banner"
              value={form.banner}
              onChange={(e) => set('banner', e.target.value)}
              fullWidth
              slotProps={{ htmlInput: { maxLength: 200 } }}
              error={Boolean(err('banner'))}
              helperText={err('banner') ?? 'Optional customer-facing line'}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              id={FIELD_PATHS.discountType}
              select
              label="Discount type"
              value={form.discountType}
              onChange={(e) => set('discountType', e.target.value as DiscountType)}
              fullWidth
              error={Boolean(err('discountType'))}
              helperText={err('discountType')}
            >
              {DISCOUNT_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {isValueType && (
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                id={FIELD_PATHS.discountValue}
                label={form.discountType === 'AMOUNT' ? 'Amount off' : 'Percent off'}
                value={form.discountValue}
                onChange={(e) => set('discountValue', e.target.value)}
                fullWidth
                required
                slotProps={{
                  htmlInput: { inputMode: 'decimal' },
                  input: {
                    startAdornment: form.discountType === 'AMOUNT' ? <InputAdornment position="start">R</InputAdornment> : undefined,
                    endAdornment: form.discountType === 'PERCENT' ? <InputAdornment position="end">%</InputAdornment> : undefined,
                  },
                }}
                error={Boolean(err('discountValue'))}
                helperText={err('discountValue') ?? (form.discountType === 'PERCENT' ? '0–100' : 'Up to two decimals')}
              />
            </Grid>
          )}
          {form.discountType === 'FREE_ITEM' && (
            <>
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  id={FIELD_PATHS.freeItemProductId}
                  label="Free item product id"
                  value={form.freeItemProductId}
                  onChange={(e) => set('freeItemProductId', e.target.value)}
                  fullWidth
                  required
                  error={Boolean(err('freeItemProductId'))}
                  helperText={err('freeItemProductId') ?? 'Product UUID'}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  id={FIELD_PATHS.freeItemQty}
                  label="Free item qty"
                  value={form.freeItemQty}
                  onChange={(e) => set('freeItemQty', e.target.value)}
                  fullWidth
                  required
                  slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                  error={Boolean(err('freeItemQty'))}
                  helperText={err('freeItemQty')}
                />
              </Grid>
            </>
          )}
          {form.discountType === 'FREE_DELIVERY' && (
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
                Free delivery has no value of its own.
              </Typography>
            </Grid>
          )}

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              id={FIELD_PATHS.maxDiscountAmount}
              label="Max discount"
              value={form.maxDiscountAmount}
              onChange={(e) => set('maxDiscountAmount', e.target.value)}
              fullWidth
              slotProps={{
                htmlInput: { inputMode: 'decimal' },
                input: { startAdornment: <InputAdornment position="start">R</InputAdornment> },
              }}
              error={Boolean(err('maxDiscountAmount'))}
              helperText={err('maxDiscountAmount') ?? 'Blank = no cap'}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              id={FIELD_PATHS.minOrderAmount}
              label="Min order"
              value={form.minOrderAmount}
              onChange={(e) => set('minOrderAmount', e.target.value)}
              fullWidth
              slotProps={{
                htmlInput: { inputMode: 'decimal' },
                input: { startAdornment: <InputAdornment position="start">R</InputAdornment> },
              }}
              error={Boolean(err('minOrderAmount'))}
              helperText={err('minOrderAmount') ?? 'Blank = no minimum'}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              id={FIELD_PATHS.priority}
              label="Priority"
              value={form.priority}
              onChange={(e) => set('priority', e.target.value)}
              fullWidth
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              error={Boolean(err('priority'))}
              helperText={err('priority') ?? '0–1000, higher wins'}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              id={FIELD_PATHS.startsAt}
              label="Starts"
              type="datetime-local"
              value={form.startsAt}
              onChange={(e) => set('startsAt', e.target.value)}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              error={Boolean(err('startsAt'))}
              helperText={err('startsAt') ?? 'Blank = immediately'}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              id={FIELD_PATHS.endsAt}
              label="Ends"
              type="datetime-local"
              value={form.endsAt}
              onChange={(e) => set('endsAt', e.target.value)}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              error={Boolean(err('endsAt'))}
              helperText={err('endsAt') ?? 'Blank = never'}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Divider>
              <Typography variant="overline">Limits</Typography>
            </Divider>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              id={FIELD_PATHS.totalRedemptionLimit}
              label="Total redemptions"
              value={form.totalRedemptionLimit}
              onChange={(e) => set('totalRedemptionLimit', e.target.value)}
              fullWidth
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              error={Boolean(err('totalRedemptionLimit'))}
              helperText={err('totalRedemptionLimit') ?? 'Blank = unlimited'}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              id={FIELD_PATHS.perCustomerLimit}
              label="Per customer"
              value={form.perCustomerLimit}
              onChange={(e) => set('perCustomerLimit', e.target.value)}
              fullWidth
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              error={Boolean(err('perCustomerLimit'))}
              helperText={err('perCustomerLimit') ?? 'Blank = unlimited'}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              id={FIELD_PATHS.perCustomerDailyLimit}
              label="Per customer per day"
              value={form.perCustomerDailyLimit}
              onChange={(e) => set('perCustomerDailyLimit', e.target.value)}
              fullWidth
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              error={Boolean(err('perCustomerDailyLimit'))}
              helperText={err('perCustomerDailyLimit') ?? 'Blank = unlimited'}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl error={Boolean(err('firstOrderOnly'))}>
              <FormControlLabel
                control={
                  <Checkbox
                    id={FIELD_PATHS.firstOrderOnly}
                    checked={form.firstOrderOnly}
                    disabled={form.returningCustomerOnly}
                    onChange={(e) => set('firstOrderOnly', e.target.checked)}
                  />
                }
                label="First order only"
              />
              {err('firstOrderOnly') && <FormHelperText>{err('firstOrderOnly')}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl error={Boolean(err('returningCustomerOnly'))}>
              <FormControlLabel
                control={
                  <Checkbox
                    id={FIELD_PATHS.returningCustomerOnly}
                    checked={form.returningCustomerOnly}
                    disabled={form.firstOrderOnly}
                    onChange={(e) => set('returningCustomerOnly', e.target.checked)}
                  />
                }
                label="Returning customers only"
              />
              {err('returningCustomerOnly') && <FormHelperText>{err('returningCustomerOnly')}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Divider>
              <Typography variant="overline">Eligibility</Typography>
            </Divider>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              id={FIELD_PATHS.eligibleProductIds}
              label="Eligible product ids"
              value={form.eligibleProductIds}
              onChange={(e) => set('eligibleProductIds', e.target.value)}
              fullWidth
              multiline
              minRows={2}
              error={Boolean(err('eligibleProductIds'))}
              helperText={err('eligibleProductIds') ?? 'One UUID per line (max 500). Blank = every product'}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              id={FIELD_PATHS.eligibleCategoryIds}
              label="Eligible category ids"
              value={form.eligibleCategoryIds}
              onChange={(e) => set('eligibleCategoryIds', e.target.value)}
              fullWidth
              multiline
              minRows={2}
              error={Boolean(err('eligibleCategoryIds'))}
              helperText={err('eligibleCategoryIds') ?? 'One UUID per line (max 100). Blank = every category'}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Divider>
              <Typography variant="overline">Stacking</Typography>
            </Divider>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControl component="fieldset" error={Boolean(err('stacking') || err('stacking.promotionIds'))} fullWidth>
              <FormLabel component="legend" id="promo-stacking-label">
                Can this promotion be combined with others?
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="promo-stacking-label"
                value={form.stackingMode}
                onChange={(e) => set('stackingMode', e.target.value as StackingMode)}
              >
                <FormControlLabel value="none" control={<Radio />} label="Don't stack" />
                <FormControlLabel value="any" control={<Radio />} label="Stack with anything" />
                <FormControlLabel value="only" control={<Radio />} label="Stack only with…" />
              </RadioGroup>
              {form.stackingMode === 'only' && (
                <Autocomplete
                  id={FIELD_PATHS.stacking}
                  multiple
                  options={partnerOptions}
                  value={selectedPartners}
                  loading={partnersQuery.isLoading}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  onChange={(_e, value) =>
                    set(
                      'stackingIds',
                      value.map((v) => v.id),
                    )
                  }
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => <Chip {...getTagProps({ index })} key={option.id} label={option.name} size="small" />)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Partner promotions"
                      placeholder="Pick one or more"
                      error={Boolean(err('stacking') || err('stacking.promotionIds'))}
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {partnersQuery.isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  )}
                  sx={{ mt: 1 }}
                />
              )}
              <FormHelperText>
                {err('stacking') ||
                  err('stacking.promotionIds') ||
                  (form.stackingMode === 'only'
                    ? onlyWithNoPartners
                      ? 'Pick at least one partner to save.'
                      : 'Only your side of the pairing is saved; the partner must allow this promotion back before they combine.'
                    : form.stackingMode === 'any'
                      ? 'Combines with any promotion that allows it back.'
                      : 'Never combines with another promotion.')}
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Box sx={{ position: 'relative' }}>
          <Button variant="contained" onClick={handleSubmit} disabled={!canSave}>
            Save
          </Button>
          {mutation.isPending && (
            <CircularProgress size={20} sx={{ position: 'absolute', top: '50%', left: '50%', mt: '-10px', ml: '-10px' }} />
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PromotionDialog;
